import { useParams, Link } from "react-router-dom";
import axios from "axios";
import "./historique.css";
import { useEffect, useMemo, useState } from "react";
import { CommandeClient, detailcommande } from "../../../../Modeles/Commande";
import SidBarClient from "../SideBarClient";
import AfficheCommandeClient from "../../../../Services/Admin/AfficheCommandeClient";
import { formatCodeCommande } from "../../../../Services/CodeTrackingHelper";
import PayArticleModal from "../../../Common/PayArticleModal";

export interface commandeType {
  product: CommandeClient[];
  messageErros: string;
}

type FilterMode = "all" | "week" | "month";

// Maps a raw status string to the pill's visual variant.
function statusVariant(statut: string) {
  if (!statut) return "is-neutral";
  const s = statut.toLowerCase().trim();
  if (s.includes("confirm") || s.includes("conffirm") || s === "valide" || s === "validé") return "is-confirme";
  if (s.includes("annul") || s.includes("refus")) return "is-annuler";
  if (s.includes("en cour") || s.includes("traitement") || s.includes("attente")) return "is-en-cour";
  return "is-neutral";
}

export default function HistoriqueCommandeClient() {
  const [state, setState] = useState<commandeType>({
    product: [] as CommandeClient[],
    messageErros: "Aucune commande",
  });
  const { id } = useParams<{ id: string }>();

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [itemToPay, setItemToPay] = useState<detailcommande | null>(null);
  const [paySuccessMsg, setPaySuccessMsg] = useState<string>("");

  const clientName = localStorage.getItem("ClientName") || localStorage.getItem("nom") || "";

  const handleChangePage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    setIsLoading(true);
    AfficheCommandeClient()
      .GetCommandeClient(id)
      .then((res) => setState((s) => ({ ...s, product: res.data })))
      .catch((msg) => setState((s) => ({ ...s, product: msg.messageErros })))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { product } = state;

  const handleShowDetails = (order: any) => {
    setSelectedOrder(order);
    setIsDetailsLoading(true);
    axios
      .get(`${process.env.REACT_APP_PHP_APP_URL}/admin/historique/${order.id}`)
      .then((res: any) => {
        setOrderDetails(res.data);
      })
      .catch((err: any) => {
        console.error(err);
        setOrderDetails([]);
      })
      .finally(() => {
        setIsDetailsLoading(false);
      });
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setOrderDetails([]);
  };

  const filteredProduct = useMemo(() => {
    if (!product || product.length === 0) return [];
    return product
      .filter((item: any) => {
        if (filterMode === "all") return true;
        const itemDate = new Date(item.Date);
        const today = new Date();
        if (filterMode === "week") {
          const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          return itemDate >= lastWeek;
        }
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return itemDate >= lastMonth;
      })
      .sort((a: any, b: any) => new Date(b.Date).getTime() - new Date(a.Date).getTime());
  }, [product, filterMode]);

  // KPI strip — derived from the currently filtered period, so the numbers
  // always match what's visible in the table below them.
  const kpis = useMemo(() => {
    const total = filteredProduct.length;
    const spent = filteredProduct.reduce((sum: number, item: any) => sum + (parseFloat(item.Total) || 0), 0);
    const confirmed = filteredProduct.filter((item: any) => {
      const s = (item.Statut || "").toLowerCase().trim();
      return s.includes("confirm") || s.includes("conffirm") || s === "valide" || s === "validé";
    }).length;
    const cancelled = filteredProduct.filter((item: any) => {
      const s = (item.Statut || "").toLowerCase().trim();
      return s.includes("annul") || s.includes("refus");
    }).length;
    return { total, spent, confirmed, cancelled };
  }, [filteredProduct]);

  // Sub-total for the currently open order's line items, used in the modal footer.
  const orderSubtotal = useMemo(() => {
    if (!orderDetails || orderDetails.length === 0) return 0;
    return orderDetails.reduce(
      (sum: number, item: any) => sum + parseFloat(item.prix || 0) * parseInt(item.quantity || 1, 10),
      0
    );
  }, [orderDetails]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProduct.slice(indexOfFirstItem, indexOfLastItem);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredProduct.length / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <>
      <SidBarClient />
      <div className="historique-page">
        <header className="hp-header">
          <h1 className="hp-title" style={{marginLeft:"-30%"}}>Historique des commandes</h1>
          <p className="hp-subtitle">
            Consultez vos commandes passées, filtrez-les par période et ouvrez le détail de
            chaque commande.
          </p>
        </header>

        {/* KPI strip */}
        <div className="hp-kpis">
          <div className="hp-kpi-card hp-fadeup" style={{ animationDelay: "0ms" }}>
            <span className="hp-kpi-label">Commandes</span>
            <span className="hp-kpi-value hp-mono">{isLoading ? "—" : kpis.total}</span>
          </div>
          <div className="hp-kpi-card hp-fadeup" style={{ animationDelay: "60ms" }}>
            <span className="hp-kpi-label">Total dépensé</span>
            <span className="hp-kpi-value hp-mono">
              {isLoading ? "—" : `${kpis.spent.toFixed(2)} MAD`}
            </span>
          </div>
          <div className="hp-kpi-card hp-fadeup is-accent" style={{ animationDelay: "120ms" }}>
            <span className="hp-kpi-label">Confirmées</span>
            <span className="hp-kpi-value hp-mono">{isLoading ? "—" : kpis.confirmed}</span>
          </div>
          <div className="hp-kpi-card hp-fadeup" style={{ animationDelay: "180ms" }}>
            <span className="hp-kpi-label">Annulées</span>
            <span className="hp-kpi-value hp-mono">{isLoading ? "—" : kpis.cancelled}</span>
          </div>
        </div>

        <div className="hp-filters" role="tablist" aria-label="Filtrer par période">
          <button
            role="tab"
            aria-selected={filterMode === "all"}
            className={`hp-filter-btn ${filterMode === "all" ? "is-active" : ""}`}
            onClick={() => {
              setFilterMode("all");
              setCurrentPage(1);
            }}
          >
            Toutes
          </button>
          <button
            role="tab"
            aria-selected={filterMode === "week"}
            className={`hp-filter-btn ${filterMode === "week" ? "is-active" : ""}`}
            onClick={() => {
              setFilterMode("week");
              setCurrentPage(1);
            }}
          >
            Cette semaine
          </button>
          <button
            role="tab"
            aria-selected={filterMode === "month"}
            className={`hp-filter-btn ${filterMode === "month" ? "is-active" : ""}`}
            onClick={() => {
              setFilterMode("month");
              setCurrentPage(1);
            }}
          >
            Ce mois
          </button>
        </div>

        <div className="hp-card">
          <table className="hp-table">
            <thead>
              <tr>
                <th scope="col">N° Commande</th>
                <th scope="col">Magasin Retrait</th>
                <th scope="col" className="hp-align-right">Montant</th>
                <th scope="col" className="hp-align-center">Statut</th>
                <th scope="col">Date</th>
                <th scope="col" className="hp-align-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="hp-skeleton-row">
                    <td colSpan={6}>
                      <div className="hp-shimmer" />
                    </td>
                  </tr>
                ))
              ) : currentItems && currentItems.length > 0 ? (
                currentItems.map((pro, index) => (
                  <tr key={index} className="hp-row hp-fadeup" style={{ animationDelay: `${index * 40}ms` }}>
                    <td data-label="N° Commande" className="hp-order-id">
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {formatCodeCommande(pro.RefCommande, pro.id, pro.Date)}
                      </span>
                    </td>
                    <td data-label="Magasin Retrait">
                      <span style={{ fontWeight: 600, color: '#2563eb', fontSize: '13px' }}>
                        {(pro as any).MagasinRetrait || (pro as any).SocieteLivraison || '—'}
                      </span>
                    </td>
                    <td data-label="Montant" className="hp-align-right">
                      <span className="hp-mono hp-amount">{pro.Total} MAD</span>
                    </td>
                    <td data-label="Statut" className="hp-align-center">
                      <span className={`hp-pill ${statusVariant(pro.Statut)}`}>{pro.Statut}</span>
                    </td>
                    <td data-label="Date" className="hp-date">{pro.Date}</td>
                    <td data-label="Action" className="hp-align-right">
                      <button className="hp-details-btn" onClick={() => handleShowDetails(pro)}>
                        Détails <i className="bi bi-arrow-right-short" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="hp-empty">
                      <i className="bi bi-inbox" />
                      <h5>Aucune commande trouvée</h5>
                      <p>Vos commandes apparaîtront ici dès votre premier achat.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {pageNumbers.length > 1 && (
            <nav className="hp-pagination" aria-label="Pagination">
              <button
                className="hp-page-btn hp-page-nav"
                onClick={() => handleChangePage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Précédent
              </button>
              {pageNumbers.map((number) => (
                <button
                  key={number}
                  className={`hp-page-btn ${currentPage === number ? "is-active" : ""}`}
                  onClick={() => handleChangePage(number)}
                >
                  {number}
                </button>
              ))}
              <button
                className="hp-page-btn hp-page-nav"
                onClick={() => handleChangePage(currentPage + 1)}
                disabled={currentPage === pageNumbers.length}
              >
                Suivant
              </button>
            </nav>
          )}
        </div>

        {/* Detail modal — rendered INSIDE .historique-page so it inherits the
            navy/amber CSS custom properties defined at that scope. */}
        {selectedOrder && (
          <div className="hp-modal-backdrop" onClick={closeModal}>
            <div
              className="hp-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="hp-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="hp-modal-head">
                <div className="hp-modal-head-text">
                  <span className="hp-eyebrow">Détail commande</span>
                  <h2 id="hp-modal-title" className="hp-modal-title">
                    Commande{" "}
                    <span className="hp-mono" style={{ fontFamily: 'monospace' }}>
                      {formatCodeCommande(selectedOrder.RefCommande, selectedOrder.id, selectedOrder.Date)}
                    </span>
                  </h2>
                </div>
                <button className="hp-modal-close" onClick={closeModal} aria-label="Fermer">
                  ✕
                </button>
              </div>

              <div className="hp-modal-meta">
                <div className="hp-meta-cell">
                  <span className="hp-meta-label">Date</span>
                  <span className="hp-meta-value">{selectedOrder.Date}</span>
                </div>
                <div className="hp-meta-cell">
                  <span className="hp-meta-label">Statut</span>
                  <span className={`hp-pill ${statusVariant(selectedOrder.Statut)}`}>
                    {selectedOrder.Statut}
                  </span>
                </div>
                <div className="hp-meta-cell">
                  <span className="hp-meta-label">Code de suivi</span>
                  <span className="hp-meta-value hp-mono" style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6366f1' }}>
                    {formatCodeCommande(selectedOrder.RefCommande, selectedOrder.id, selectedOrder.Date)}
                  </span>
                </div>
                <div className="hp-meta-cell hp-meta-cell-right">
                  <span className="hp-meta-label">Montant total</span>
                  <span className="hp-meta-value hp-mono hp-meta-value-lg">
                    {selectedOrder.Total} MAD
                  </span>
                </div>
              </div>

              <div className="hp-modal-body">
                <div className="hp-modal-section-label">Articles</div>

                {isDetailsLoading ? (
                  <div className="hp-modal-skeleton">
                    <div className="hp-shimmer-line" />
                    <div className="hp-shimmer-line" />
                    <div className="hp-shimmer-line" style={{ width: "70%" }} />
                  </div>
                ) : orderDetails && orderDetails.length > 0 ? (
                  <table className="hp-items-table">
                    <thead>
                      <tr>
                        <th scope="col">Article</th>
                        <th scope="col" className="hp-align-center">Qté</th>
                        <th scope="col" className="hp-align-right">Prix unit.</th>
                        <th scope="col" className="hp-align-right">Total</th>
                        <th scope="col">Vendeur</th>
                        <th scope="col" className="hp-align-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails.map((item: any, idx: number) => {
                        const sLower = (item.Statut || "").toLowerCase();
                        const isPaid = sLower.includes("pay") || sLower.includes("confirm");
                        const isCancelled = sLower.includes("annul");
                        return (
                          <tr key={idx}>
                            <td>
                              <span className="hp-li-name">{item.NomArticle}</span>
                              <span className="hp-li-ref">Réf. {item.RefArticle}</span>
                            </td>
                            <td className="hp-align-center hp-mono">{item.quantity}</td>
                            <td className="hp-align-right hp-mono">{item.prix} MAD</td>
                            <td className="hp-align-right hp-mono hp-li-total">
                              {(parseFloat(item.prix || 0) * parseInt(item.quantity || 1, 10)).toFixed(2)} MAD
                            </td>
                            <td>
                              <span style={{ fontSize: "12px", color: "#475569" }}>
                                {item.vendeur || "—"}
                              </span>
                            </td>
                            <td className="hp-align-center">
                              {isCancelled ? (
                                <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 500 }}>—</span>
                              ) : isPaid ? (
                                <span style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  backgroundColor: "#f0fdf4",
                                  color: "#16a34a",
                                  border: "1px solid rgba(34,197,94,0.3)",
                                  padding: "4px 10px",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                }}>
                                  ✓ Payé
                                </span>
                              ) : (
                                <button
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    backgroundColor: "#2563eb",
                                    color: "#ffffff",
                                    border: "none",
                                    padding: "7px 14px",
                                    borderRadius: "8px",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
                                    transition: "all 0.2s",
                                  }}
                                  onClick={() => setItemToPay(item)}
                                >
                                  💳 Payer
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="hp-modal-empty">
                    <i className="bi bi-inbox" />
                    Aucun détail trouvé pour cette commande.
                  </div>
                )}
              </div>

              {!isDetailsLoading && orderDetails && orderDetails.length > 0 && (
                <div className="hp-modal-totals">
                  <div className="hp-totals-row">
                    <span>Sous-total</span>
                    <span className="hp-mono">{orderSubtotal.toFixed(2)} MAD</span>
                  </div>
                  <div className="hp-totals-row hp-totals-row-final">
                    <span>Total</span>
                    <span className="hp-mono">{selectedOrder.Total} MAD</span>
                  </div>
                </div>
              )}

              {paySuccessMsg && (
                <div style={{
                  margin: "0 16px 12px 16px",
                  padding: "10px 14px",
                  backgroundColor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  color: "#166534",
                  fontSize: "13px",
                  fontWeight: 600,
                }}>
                  ✓ {paySuccessMsg}
                </div>
              )}

              <div className="hp-modal-foot">
                <Link
                  to={`/clients/${id}/historique/${selectedOrder.id}/rembourser`}
                  className="hp-btn-refund"
                >
                  <i className="bi bi-arrow-counterclockwise" /> Rembourser
                </Link>
                <button className="hp-btn-ghost" onClick={closeModal}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pay Article Modal */}
        <PayArticleModal
          isOpen={!!itemToPay}
          onClose={() => setItemToPay(null)}
          item={itemToPay}
          buyerType="client"
          buyerId={localStorage.getItem("ClientId") || id}
          buyerName={clientName}
          onSuccess={(newStatut, newSolde) => {
            if (itemToPay) {
              setOrderDetails((prev) =>
                prev.map((p) =>
                  p.id === itemToPay.id ? { ...p, Statut: newStatut } : p
                )
              );
            }
            if (newSolde !== undefined && newSolde !== null) {
              localStorage.setItem("en_compte_solde", String(newSolde));
            }
            setPaySuccessMsg(`Article "${itemToPay?.NomArticle}" payé avec succès.`);
            setTimeout(() => setPaySuccessMsg(""), 4000);
          }}
        />
      </div>
    </>
  );
}